import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, MoreHorizontal, Edit2, Trash2, Instagram, Youtube, Globe, X, RefreshCw, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { useApp, CONTENT_TYPES, PLATFORMS } from '@/context/AppContext';
import { toast } from 'sonner';

const BRAND_COLORS = ['#2F7BFF', '#22D3EE', '#29A874', '#F5A623', '#E53E3E', '#9B59B6', '#E91E8C'];

const DEFAULT_CREATORS_BY_TYPE = {
  premium_creator: ['Sophie Rain', 'Corinna Kopf', 'Bryce Adams', 'Amouranth', 'Mia Khalifa'],
  fitness: ['Sam Sulek', 'David Laid', 'Noel Deyzel', 'Chris Bumstead', 'LeanBeefPatty'],
  coaching: ['Alex Hormozi', 'Iman Gadzhi', 'Dan Koe', 'Gary Vee', 'Andrew Huberman'],
  educational: ['Ali Abdaal', 'Mark Rober', 'Veritasium', 'MrWhoseTheBoss'],
  lifestyle: ['Emma Chamberlain', 'Alix Earle', 'Nara Smith', 'Olivia Neill'],
  entertainment: ['MrBeast', 'Ryan Trahan', 'Khaby Lame', 'Kai Cenat'],
  personal_brand: ['Alex Becker', 'Naval', 'Tim Ferriss', 'Lex Fridman'],
};

const PlatformTag = ({ platform }) => {
  const colors = { instagram: '#E1306C', tiktok: '#69C9D0', youtube: '#FF0000', x: '#9AA4B2', facebook: '#1877F2' };
  const labels = { instagram: 'IG', tiktok: 'TK', youtube: 'YT', x: 'X', facebook: 'FB' };
  return (
    <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: `${colors[platform]}18`, color: colors[platform] }}>
      {labels[platform]}
    </span>
  );
};

// Reusable tag input for string arrays
function TagInput({ items, onAdd, onRemove, placeholder, inputValue, onInputChange }) {
  const handleKey = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      onAdd(inputValue.trim());
    }
  };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          className="reelia-input flex-1 h-8 text-xs"
          placeholder={placeholder}
          value={inputValue}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={handleKey}
        />
        <Button size="sm" variant="ghost" className="h-8 px-3 text-xs flex-shrink-0"
          style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}
          onClick={() => { if (inputValue.trim()) { onAdd(inputValue.trim()); } }}
        >
          Add
        </Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((item, i) => (
            <span key={i} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))', border: '1px solid hsl(var(--primary) / 0.3)' }}>
              {item}
              <button onClick={() => onRemove(i)} style={{ color: 'hsl(var(--primary))' }}>
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Brands() {
  const { creators, addBrand, updateBrand, removeBrand, setActiveBrandId, activeCreatorId, setActiveCreatorId } = useApp();

  // Add dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [selectedCreatorId, setSelectedCreatorId] = useState(activeCreatorId);
  const [brandName, setBrandName] = useState('');
  const [contentType, setContentType] = useState('');
  const [selectedColor, setSelectedColor] = useState(BRAND_COLORS[0]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editBrand, setEditBrand] = useState(null);
  const [editName, setEditName] = useState('');
  const [editContentType, setEditContentType] = useState('');
  const [editRefCreators, setEditRefCreators] = useState([]);
  const [editRefVideos, setEditRefVideos] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [refCreatorInput, setRefCreatorInput] = useState('');
  const [refVideoInput, setRefVideoInput] = useState('');

  // Filter brands by active creator
  const filteredCreator = creators.find(c => c.id === activeCreatorId) || creators[0] || null;
  const allBrandsWithCreator = filteredCreator
    ? (filteredCreator.brands || []).map(b => ({ ...b, creatorName: filteredCreator.name, creatorId: filteredCreator.id, creatorAvatar: filteredCreator.avatar }))
    : [];

  const togglePlatform = (pid) => {
    setSelectedPlatforms(prev => prev.includes(pid) ? prev.filter(p => p !== pid) : [...prev, pid]);
  };

  const openEdit = (brand) => {
    setEditBrand(brand);
    setEditName(brand.name);
    setEditContentType(brand.content_type || brand.contentType || '');
    setEditRefCreators(brand.reference_creators || []);
    setEditRefVideos(brand.reference_videos || []);
    setRefCreatorInput('');
    setRefVideoInput('');
    setEditOpen(true);
  };

  const handleAdd = async () => {
    if (!brandName.trim() || !contentType || !selectedCreatorId) return;
    try {
      await addBrand(selectedCreatorId, {
        name: brandName.trim(),
        content_type: contentType,
        color: selectedColor,
        platforms: selectedPlatforms,
      });
      setBrandName(''); setContentType(''); setSelectedPlatforms([]);
      setAddOpen(false);
      toast.success(`Brand "${brandName.trim()}" created`);
    } catch (e) {
      toast.error(e.message || 'Failed to create brand');
    }
  };

  const handleSave = async () => {
    if (!editName.trim() || !editContentType || !editBrand) return;
    setEditSaving(true);
    try {
      await updateBrand(editBrand.id, editBrand.creatorId, {
        name: editName.trim(),
        content_type: editContentType,
        reference_creators: editRefCreators,
        reference_videos: editRefVideos,
      });
      setEditOpen(false);
      toast.success('Brand updated');
    } catch (e) {
      toast.error(e.message || 'Failed to update brand');
    } finally {
      setEditSaving(false);
    }
  };

  const defaultCreators = DEFAULT_CREATORS_BY_TYPE[editContentType] || [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Brands</h2>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--foreground-muted))' }}>
            {allBrandsWithCreator.length} brand{allBrandsWithCreator.length !== 1 ? 's' : ''}
            {filteredCreator ? ` for ${filteredCreator.name}` : ''}
          </p>
        </div>
        <Button size="sm" className="gap-1.5 text-xs" onClick={() => setAddOpen(true)}
          style={{ background: 'hsl(var(--primary))', color: '#fff' }}
        >
          <Plus size={14} /> New Brand
        </Button>
      </div>

      {/* Creator filter tabs */}
      {creators.length > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mb-2">
          {creators.map(c => (
            <button
              key={c.id}
              onClick={() => setActiveCreatorId(c.id)}
              data-testid={`creator-filter-${c.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium flex-shrink-0 transition-all"
              style={{
                background: activeCreatorId === c.id ? 'hsl(var(--primary) / 0.12)' : 'hsl(var(--surface))',
                border: `1px solid ${activeCreatorId === c.id ? 'hsl(var(--primary) / 0.5)' : 'hsl(var(--border))'}`,
                color: activeCreatorId === c.id ? 'hsl(var(--primary))' : 'hsl(var(--foreground-muted))',
              }}
            >
              <div className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: 'hsl(var(--surface-elevated))' }}>
                {c.name.slice(0, 1).toUpperCase()}
              </div>
              {c.name}
              <span className="opacity-60">({(c.brands || []).length})</span>
            </button>
          ))}
        </div>
      )}

      {/* Brands grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {allBrandsWithCreator.map((brand, idx) => (
          <motion.div
            key={brand.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.07 }}
            className="reelia-card-glow p-5 flex flex-col"
            data-testid={`brand-card-${idx}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ background: `${brand.color}20`, border: `1px solid ${brand.color}40` }}>
                  <span style={{ color: brand.color }}>{brand.name.slice(0, 2).toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>{brand.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-subtle))' }}>by {brand.creatorName}</p>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 rounded hover:bg-surface-hover" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    <MoreHorizontal size={15} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setActiveBrandId(brand.id); setActiveCreatorId(brand.creatorId); }}>
                    <Globe size={13} className="mr-2" /> Set as Active
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openEdit(brand)} data-testid={`edit-brand-${idx}`}>
                    <Edit2 size={13} className="mr-2" /> Edit Brand
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={() => removeBrand(brand.creatorId, brand.id)}>
                    <Trash2 size={13} className="mr-2" /> Delete Brand
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="mb-3">
              <span className="text-xs px-2.5 py-1 rounded-full font-medium"
                style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}>
                {CONTENT_TYPES.find(ct => ct.id === (brand.content_type || brand.contentType))?.icon}
                {' '}{CONTENT_TYPES.find(ct => ct.id === (brand.content_type || brand.contentType))?.label || brand.content_type || brand.contentType}
              </span>
            </div>

            {/* Reference creators row */}
            {(brand.reference_creators || []).length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium mb-1.5" style={{ color: 'hsl(var(--foreground-subtle))' }}>REFERENCE CREATORS</p>
                <div className="flex flex-wrap gap-1">
                  {brand.reference_creators.slice(0, 3).map(rc => (
                    <span key={rc} className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: 'hsl(var(--primary) / 0.08)', color: 'hsl(var(--primary))', border: '1px solid hsl(var(--primary) / 0.2)' }}>
                      {rc}
                    </span>
                  ))}
                  {brand.reference_creators.length > 3 && (
                    <span className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: 'hsl(var(--surface))', color: 'hsl(var(--foreground-subtle))' }}>
                      +{brand.reference_creators.length - 3}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="flex-1">
              <p className="text-xs font-medium mb-2" style={{ color: 'hsl(var(--foreground-subtle))' }}>CONNECTED PLATFORMS</p>
              <div className="flex flex-wrap gap-1.5">
                {brand.platforms.length === 0 && (
                  <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>None connected</span>
                )}
                {brand.platforms.map(p => <PlatformTag key={p} platform={p} />)}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4 pt-4" style={{ borderTop: '1px solid hsl(var(--border-muted))' }}>
              {[
                { label: 'Posts', value: Math.floor(Math.random() * 60 + 10) },
                { label: 'Followers', value: `${(Math.random() * 50 + 2).toFixed(1)}K` },
                { label: 'Eng. Rate', value: `${(Math.random() * 8 + 2).toFixed(1)}%` },
              ].map(m => (
                <div key={m.label} className="text-center">
                  <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>{m.value}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{m.label}</p>
                </div>
              ))}
            </div>

            <Button
              variant="ghost" size="sm" className="w-full mt-3 text-xs h-8"
              onClick={() => { setActiveBrandId(brand.id); setActiveCreatorId(brand.creatorId); }}
              style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}
            >
              Switch to Brand
            </Button>
          </motion.div>
        ))}

        {/* Add brand card */}
        <button onClick={() => setAddOpen(true)}
          className="reelia-card p-5 flex flex-col items-center justify-center gap-3 min-h-48"
          style={{ borderStyle: 'dashed', opacity: 0.6 }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(var(--surface-elevated))' }}>
            <Plus size={18} style={{ color: 'hsl(var(--foreground-muted))' }} />
          </div>
          <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>New Brand</p>
        </button>
      </div>

      {/* Add Brand Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Brand</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Creator *</label>
              <Select value={selectedCreatorId} onValueChange={setSelectedCreatorId}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select creator" /></SelectTrigger>
                <SelectContent>
                  {creators.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Brand Name *</label>
              <input className="reelia-input" placeholder="e.g. AlexFit" value={brandName} onChange={e => setBrandName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Content Type *</label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Choose content type" /></SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.icon} {ct.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Brand Color</label>
              <div className="flex gap-2">
                {BRAND_COLORS.map(c => (
                  <button key={c} onClick={() => setSelectedColor(c)}
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: c, outline: selectedColor === c ? `2px solid ${c}` : 'none', outlineOffset: '2px' }} />
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs mb-2 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Platforms</label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(p => (
                  <button key={p.id} onClick={() => togglePlatform(p.id)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{
                      background: selectedPlatforms.includes(p.id) ? 'hsl(var(--primary) / 0.15)' : 'hsl(var(--surface))',
                      border: `1px solid ${selectedPlatforms.includes(p.id) ? 'hsl(var(--primary) / 0.5)' : 'hsl(var(--border))'}`,
                      color: selectedPlatforms.includes(p.id) ? 'hsl(var(--primary))' : 'hsl(var(--foreground-muted))'
                    }}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={!brandName.trim() || !contentType} onClick={handleAdd}
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
              Create Brand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Brand Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Creator (read-only) */}
            {editBrand && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
                <div className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: `${editBrand.color}20`, color: editBrand.color }}>
                  {editBrand.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>Creator (read-only)</p>
                  <p className="text-sm font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>
                    {editBrand.creatorName}
                  </p>
                </div>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Brand Name *</label>
              <input className="reelia-input" placeholder="Brand name"
                value={editName} onChange={e => setEditName(e.target.value)} />
            </div>

            {/* Content Type */}
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Content Type *</label>
              <Select value={editContentType} onValueChange={setEditContentType}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Choose content type" /></SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.icon} {ct.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Connected Platforms (read-only) */}
            {editBrand?.platforms?.length > 0 && (
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Connected Platforms (read-only)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {editBrand.platforms.map(p => <PlatformTag key={p} platform={p} />)}
                </div>
              </div>
            )}

            {/* Divider */}
            <div style={{ borderTop: '1px solid hsl(var(--border-muted))' }} />

            {/* Reference Creators */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Reference Creators
                </label>
                <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                  Used to guide AI hook style & tone
                </span>
              </div>
              <TagInput
                items={editRefCreators}
                onAdd={(val) => { if (!editRefCreators.includes(val)) setEditRefCreators(p => [...p, val]); setRefCreatorInput(''); }}
                onRemove={(i) => setEditRefCreators(p => p.filter((_, idx) => idx !== i))}
                placeholder="e.g. Alex Hormozi"
                inputValue={refCreatorInput}
                onInputChange={setRefCreatorInput}
              />
              {/* Defaults hint */}
              {defaultCreators.length > 0 && editRefCreators.length === 0 && (
                <div className="mt-2">
                  <p className="text-xs mb-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    Defaults for {CONTENT_TYPES.find(ct => ct.id === editContentType)?.label}:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {defaultCreators.map(dc => (
                      <button key={dc}
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          background: 'hsl(var(--surface))',
                          color: 'hsl(var(--foreground-subtle))',
                          border: '1px solid hsl(var(--border))'
                        }}
                        onClick={() => setEditRefCreators(p => p.includes(dc) ? p : [...p, dc])}>
                        + {dc}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Reference Videos */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Reference Videos
                </label>
                <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                  Optional — YouTube/TikTok URLs
                </span>
              </div>
              <TagInput
                items={editRefVideos}
                onAdd={(val) => { setEditRefVideos(p => [...p, val]); setRefVideoInput(''); }}
                onRemove={(i) => setEditRefVideos(p => p.filter((_, idx) => idx !== i))}
                placeholder="https://youtube.com/watch?v=..."
                inputValue={refVideoInput}
                onInputChange={setRefVideoInput}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button size="sm"
              disabled={!editName.trim() || !editContentType || editSaving}
              onClick={handleSave}
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}
              data-testid="save-edit-brand-btn">
              {editSaving
                ? <><RefreshCw size={12} className="animate-spin mr-1.5" /> Saving...</>
                : 'Save Changes'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
